import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ArrowLeft, LoaderCircle, Pipette } from "lucide-react";
import { HexColorPicker } from "react-colorful";
import { toast } from "react-toastify";
import { Button } from "../../misc/Button";
import { Card, CardContent, CardHeader, CardTitle } from "../../misc/Card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../misc/Dialog";
import { Label } from "../../misc/Label";
import HexCodeInput from "../../misc/HexCodeInput";
import CustomToast from "../../misc/CustomToast";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { getAuthHeader } from "../../../utils/authHeader";
import LoadingScreen from "../../../screens/LoadingScreen";
import { normalizeColorToHex } from "./colorUtils";
import { useNavigate } from "react-router-dom";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

type BrandingSettingsValues = {
    mainColor: string;
    secondColor: string;
    thirdColor: string;
};

type BrandingField = keyof BrandingSettingsValues;

type BrandingHexState = Record<BrandingField, string>;

type BrandingFieldMeta = {
    key: BrandingField;
    label: string;
};

const FIELD_CONFIG: BrandingFieldMeta[] = [
    {
        key: "mainColor",
        label: "Primary",
    },
    {
        key: "secondColor",
        label: "Secondary",
    },
    {
        key: "thirdColor",
        label: "Accent",
    },
];

const DEFAULT_VALUES: BrandingSettingsValues = {
    mainColor: "#A08747",
    secondColor: "#0F3930",
    thirdColor: "#FFFFFF",
};

const toHexState = (values: BrandingSettingsValues): BrandingHexState => ({
    mainColor: normalizeColorToHex(values.mainColor) ?? DEFAULT_VALUES.mainColor,
    secondColor:
        normalizeColorToHex(values.secondColor) ?? DEFAULT_VALUES.secondColor,
    thirdColor: normalizeColorToHex(values.thirdColor) ?? DEFAULT_VALUES.thirdColor,
});

const normalizeSettingsPayload = (
    values: Partial<BrandingSettingsValues>,
): BrandingSettingsValues => ({
    mainColor:
        normalizeColorToHex(values.mainColor ?? DEFAULT_VALUES.mainColor) ??
        DEFAULT_VALUES.mainColor,
    secondColor:
        normalizeColorToHex(values.secondColor ?? DEFAULT_VALUES.secondColor) ??
        DEFAULT_VALUES.secondColor,
    thirdColor:
        normalizeColorToHex(values.thirdColor ?? DEFAULT_VALUES.thirdColor) ??
        DEFAULT_VALUES.thirdColor,
});

const BrandingSettings = () => {
    const navigate = useNavigate();
    const isMobile = useIsMobile(1200);
    const [viewportWidth, setViewportWidth] = useState(() =>
        typeof window !== "undefined" ? window.innerWidth : 0,
    );

    const [baselineValues, setBaselineValues] =
        useState<BrandingSettingsValues>(DEFAULT_VALUES);
    const [hexValues, setHexValues] = useState<BrandingHexState>(
        toHexState(DEFAULT_VALUES),
    );
    const [fieldErrors, setFieldErrors] = useState<
        Partial<Record<BrandingField, string>>
    >({});
    const [activeDialogField, setActiveDialogField] =
        useState<BrandingField | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const previewValues = useMemo(
        () => ({
            mainColor:
                normalizeColorToHex(hexValues.mainColor) ??
                baselineValues.mainColor,
            secondColor:
                normalizeColorToHex(hexValues.secondColor) ??
                baselineValues.secondColor,
            thirdColor:
                normalizeColorToHex(hexValues.thirdColor) ??
                baselineValues.thirdColor,
        }),
        [baselineValues, hexValues],
    );

    const hasValidationErrors = Object.values(fieldErrors).some(Boolean);

    const openFieldPicker = (field: BrandingField) => {
        setActiveDialogField(field);
    };

    const closeFieldPicker = () => {
        setActiveDialogField(null);
    };

    const colorGridColumns =
        viewportWidth >= 1200
            ? "repeat(3, minmax(0, 1fr))"
            : viewportWidth >= 768
              ? "repeat(2, minmax(0, 1fr))"
              : "repeat(1, minmax(0, 1fr))";

    useEffect(() => {
        const handleResize = () => {
            setViewportWidth(window.innerWidth);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const fetchBrandingSettings = async () => {
            try {
                setLoading(true);
                const headers = await getAuthHeader();
                const response = await axios.get(
                    `${backendUrl}/settings/branding`,
                    {
                        headers,
                    },
                );
                const normalizedValues = normalizeSettingsPayload(
                    response.data ?? {},
                );
                setBaselineValues(normalizedValues);
                setHexValues(toHexState(normalizedValues));
                setFieldErrors({});
            } catch (error) {
                console.error("Failed to fetch branding settings:", error);
                toast.error(
                    <CustomToast
                        message="Failed to load branding settings"
                        type="error"
                    />,
                    { autoClose: 3000 },
                );
            } finally {
                setLoading(false);
            }
        };

        fetchBrandingSettings();
    }, []);

    const validateField = (field: BrandingField, value: string) => {
        const normalizedValue = normalizeColorToHex(value);
        setFieldErrors((currentErrors) => {
            const nextErrors = { ...currentErrors };

            if (!value.trim()) {
                nextErrors[field] = "This field is required.";
            } else if (!normalizedValue) {
                nextErrors[field] = "Enter a valid HEX color.";
            } else {
                delete nextErrors[field];
            }

            return nextErrors;
        });

        return normalizedValue;
    };

    // The hex box only ever hands over a finished #RRGGBB, so store it as is.
    const handleHexInputChange = (field: BrandingField, value: string) => {
        setHexValues((currentValues) => ({
            ...currentValues,
            [field]: value,
        }));
        setFieldErrors((currentErrors) => {
            const nextErrors = { ...currentErrors };
            delete nextErrors[field];
            return nextErrors;
        });
    };

    const handlePickerChange = (field: BrandingField, value: string) => {
        setHexValues((currentValues) => ({
            ...currentValues,
            [field]: value.toUpperCase(),
        }));
        setFieldErrors((currentErrors) => {
            const nextErrors = { ...currentErrors };
            delete nextErrors[field];
            return nextErrors;
        });
    };

    const handleSave = async () => {
        const normalizedValues = {} as BrandingSettingsValues;
        let hasErrors = false;

        (Object.keys(hexValues) as BrandingField[]).forEach((field) => {
            const normalizedValue = normalizeColorToHex(hexValues[field]);
            if (!normalizedValue) {
                hasErrors = true;
                validateField(field, hexValues[field]);
                return;
            }
            normalizedValues[field] = normalizedValue;
        });

        if (hasErrors) {
            return;
        }
        try {
            setSaving(true);
            const headers = await getAuthHeader();
            const response = await axios.patch(
                `${backendUrl}/settings/branding`,
                normalizedValues,
                { headers },
            );

            const savedValues = normalizeSettingsPayload(
                response.data ?? normalizedValues,
            );
            setBaselineValues(savedValues);
            setHexValues(toHexState(savedValues));
            setFieldErrors({});

            toast.success(
                <CustomToast
                    message="Branding settings updated successfully"
                    type="success"
                />,
                { autoClose: 3000 },
            );
        } catch (error) {
            console.error("Failed to update branding settings:", error);
            toast.error(
                <CustomToast
                    message="Failed to update branding settings"
                    type="error"
                />,
                { autoClose: 3000 },
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={`flex justify-center ${isMobile ? "h-full" : "mt-4"}`}>
            <Card
                className={`w-full mx-auto ${
                    isMobile
                        ? "h-full m-0 rounded-none max-w-none"
                        : "h-auto max-w-4xl"
                }`}
            >
                <div className="p-4">
                    <Button
                        id="back-to-settings-btn"
                        onClick={() => navigate("/settings")}
                        className="mb-4 flex items-center"
                    >
                        <ArrowLeft className="mr-2 h-5 w-5" /> Back to Settings
                    </Button>
                </div>
                <CardHeader>
                    <CardTitle className="text-left">Branding</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                    {loading ? (
                        <LoadingScreen />
                    ) : (
                        <>
                            <div className="text-sm font-semibold tracking-wide text-neutral-600">
                                Branding colors
                            </div>
                            <div className="grid gap-6">
                                <div
                                    className="grid gap-4"
                                    style={{
                                        gridTemplateColumns: colorGridColumns,
                                    }}
                                >
                                    {FIELD_CONFIG.map((field) => (
                                        <div
                                            key={field.key}
                                            className="w-full rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm"
                                        >
                                            <Label className="text-xs font-semibold uppercase tracking-[0.08em] text-neutral-600">
                                                {field.label}
                                            </Label>

                                            <div
                                                className="relative mt-3 h-20 w-full overflow-hidden rounded-xl border border-neutral-200"
                                                style={{
                                                    backgroundColor: previewValues[field.key],
                                                }}
                                            >
                                                <div className="absolute inset-0 flex items-center justify-center">
                                                    <button
                                                        type="button"
                                                        onClick={() => openFieldPicker(field.key)}
                                                        aria-label={`Edit ${field.label} color`}
                                                        className="flex h-10 w-10 items-center justify-center rounded-full border border-neutral-300 bg-white/80 shadow-sm hover:bg-white"
                                                    >
                                                        <Pipette className="h-4 w-4 text-neutral-600" />
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="mt-3 grid grid-cols-[44px_1fr] items-center gap-2">
                                                <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-neutral-500">
                                                    Hex
                                                </span>
                                                <div className="h-10 w-full rounded-md border border-neutral-200 bg-neutral-50 px-3 font-mono text-sm flex items-center text-neutral-700">
                                                    {previewValues[field.key]}
                                                </div>
                                            </div>

                                            {fieldErrors[field.key] ? (
                                                <p className="mt-2 text-sm text-red-600">
                                                    {fieldErrors[field.key]}
                                                </p>
                                            ) : null}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="flex justify-end">
                                <Button
                                    id="save-branding-settings-btn"
                                    onClick={handleSave}
                                    disabled={hasValidationErrors || saving}
                                    className={isMobile ? "w-full" : "w-auto"}
                                >
                                    {saving ? (
                                        <>
                                            <LoaderCircle className="animate-spin h-5 w-5 mr-2" />
                                            Saving...
                                        </>
                                    ) : (
                                        "Save"
                                    )}
                                </Button>
                            </div>

                            <Dialog
                                open={Boolean(activeDialogField)}
                                onOpenChange={(open) => {
                                    if (!open) {
                                        closeFieldPicker();
                                    }
                                }}
                            >
                                <DialogContent className="max-w-md bg-white">
                                    <DialogHeader className="pb-2">
                                        <DialogTitle>
                                            Edit {activeDialogField
                                                ? FIELD_CONFIG.find((f) => f.key === activeDialogField)?.label
                                                : "Color"}
                                        </DialogTitle>
                                    </DialogHeader>

                                    {activeDialogField ? (
                                        <div className="mt-2 space-y-4">
                                            <HexColorPicker
                                                color={previewValues[activeDialogField]}
                                                onChange={(value) =>
                                                    handlePickerChange(activeDialogField, value)
                                                }
                                                className="!w-full"
                                            />

                                            <div className="grid grid-cols-[44px_1fr] items-center gap-2">
                                                <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-neutral-500">
                                                    Hex
                                                </span>
                                                <HexCodeInput
                                                    aria-label="Selected color hex input"
                                                    color={hexValues[activeDialogField]}
                                                    onChange={(value) =>
                                                        handleHexInputChange(activeDialogField, value)
                                                    }
                                                    className="h-10 w-full rounded-md border border-neutral-200 bg-neutral-50 px-3 text-sm placeholder:text-neutral-400"
                                                />
                                            </div>

                                            <div className="flex justify-end">
                                                <Button type="button" onClick={closeFieldPicker}>
                                                    Done
                                                </Button>
                                            </div>
                                        </div>
                                    ) : null}
                                </DialogContent>
                            </Dialog>
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default BrandingSettings;
